import { SubtitleActionTextButton } from '@shared/components/core/alertNotification/CommonAlertNotificationStyles';
import Button from '@shared/components/core/button/CoreButton';
import Icons from '@shared/components/core/images/Icons';
import { LightXSmall } from '@shared/components/core/text/Typography';
import { FeatureItem, FeaturesList, FeaturesSection } from '@shared/components/license/CommonFeatureListStyles';
import { premiumFeatures } from '@shared/constants/product/ProductUtils';
import { IBannerModel } from '@shared/models/alerts/IBannerModel';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useCurrentUserStore } from '../state/CurrentUserContainer';
import { useOrganizationStore } from '../state/OrganizationContainer';
import { useCurrentTrial } from '../useCurrentTrialFetching';
import { getUpgradeEntryPath } from '@shared/components/organization/billing/upgradeNavigation';

// todo: consider refactoring to use useRelativeTime or some similar logic
export default function useFreeTrialStatusBanner() {
    const { t } = useTranslation();
    const { organizationId, managedOrgId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { removeUpgradeBanner, addUpgradeBanner } = useCurrentUserStore.getState();
    const currentUser = useCurrentUserStore((state) => state.currentUser);
    const userLicenseAccess = useCurrentUserStore((state) => state.userLicenseAccess);
    const memberships = useOrganizationStore((state) => state.memberships);
    const currentOrg = useOrganizationStore((state) => state.currentOrg);
    const { mostRecentTrial, freeTrialOrg, canManageBilling } = useCurrentTrial();

    const today = new Date();
    const daysLeft = mostRecentTrial
        ? Math.ceil((new Date(mostRecentTrial.expirationDate).getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
    const isOnHomePage = location.pathname === '/';

    const getTrialProductName = () => {
        if (!mostRecentTrial) return '';
        // this logic is based on current product naming conventions and may need to be updated if new products are added or names change
        if (mostRecentTrial.isEmployeeAccess || mostRecentTrial.isStudentAccess) {
            return t('common:billing.isEmployeeAccess.titleLong');
        } else if (mostRecentTrial.isPlusAccess) {
            return t('common:billing.isPlusAccess.titleLong');
        } else {
            return 'Cephable';
        }
    };

    const handleFreeTrial = useCallback(async () => {
        // validation
        if (!freeTrialOrg) {
            handleFreeTrialExpiration();
            return;
        }

        // remaining users calculation
        const maxMembers = freeTrialOrg?.organization.maxMembers ?? 0;

        let currMembers = 0;
        if (currentOrg?.id === freeTrialOrg.organizationId) {
            currMembers = currentOrg.currentMemberCount ?? 0;
        } else {
            const result = await useOrganizationStore.getState().getOrganizationDetails(freeTrialOrg.organizationId);
            currMembers = result?.resultType === 'Ok' && result.data ? (result.data.currentMemberCount ?? 0) : 0;
        }
        const remainingUsers = Math.max(0, maxMembers - currMembers);
        const isMultiUserOrg = maxMembers > 1;

        const handleUpgrade = () => {
            // upgrade button
            if (isOnHomePage && isMultiUserOrg) {
                navigate(`/org/${freeTrialOrg.organizationId}`);
            } else {
                navigate(getUpgradeEntryPath({ organizationId: freeTrialOrg.organizationId }));
            }
        };

        const ActionComponent = canManageBilling && (
            <Button $size="small" startIcon="upgrade" onClick={handleUpgrade}>
                {t('common:upgrade')}
            </Button>
        );

        // current org member
        const canInviteMembers = freeTrialOrg?.canInviteMembers || freeTrialOrg?.isAdmin;

        let subtitle: IBannerModel['subtitle'] =
            remainingUsers == 0
                ? t('common:license.trial.noUsersSubtitle')
                : t('common:license.trial.subtitle', { count: remainingUsers });
        if (maxMembers === 1) {
            subtitle = (
                <FeaturesSection>
                    <LightXSmall>{t('billing.upgrade.longDescription')}</LightXSmall>
                    <FeaturesList>
                        {premiumFeatures.map((feature, index) => (
                            <FeatureItem key={index}>
                                <Icons name="check" size={16} />
                                <LightXSmall>{t(feature)}</LightXSmall>
                            </FeatureItem>
                        ))}
                    </FeaturesList>
                </FeaturesSection>
            );
        }

        // prevent inviting members if email is not verified
        const SubtitleAction =
            remainingUsers > 0 && canInviteMembers ? (
                <SubtitleActionTextButton
                    disabled={currentUser?.isEmailVerified == false}
                    $color="context"
                    to={`/org/${freeTrialOrg.organizationId}/members/invites`}>
                    {t('common:organization.canInviteMembers.title')}
                </SubtitleActionTextButton>
            ) : null;

        const title =
            memberships.length > 1 && isOnHomePage
                ? t('common:license.trial.title.multiple', {
                      days: daysLeft,
                      organization: freeTrialOrg.organization.name,
                      productName: getTrialProductName(),
                  })
                : t('common:license.trial.title.single', { days: daysLeft, productName: getTrialProductName() });

        const freeTrialBanner: IBannerModel = {
            id: 'freeTrial',
            title: title,
            subtitle: subtitle,
            children: ActionComponent,
            subtitleAction: SubtitleAction,
        };

        addUpgradeBanner(freeTrialBanner);
    }, [addUpgradeBanner, t, freeTrialOrg, mostRecentTrial]);

    const handleFreeTrialExpiration = useCallback(() => {
        removeUpgradeBanner('freeTrial');
    }, [removeUpgradeBanner]);

    useEffect(() => {
        if (userLicenseAccess === null || userLicenseAccess === undefined) {
            return;
        }
        const currentUser = useCurrentUserStore.getState().currentUser;
        const isTrial = currentUser && freeTrialOrg && mostRecentTrial && daysLeft > 0 && !userLicenseAccess.hasLicense;

        const isValidPage =
            isOnHomePage ||
            (organizationId &&
                ((freeTrialOrg?.organizationId === organizationId && !managedOrgId) ||
                    freeTrialOrg?.organizationId === managedOrgId));

        if (isTrial && isValidPage && !useCurrentUserStore.getState().isFreeUser()) {
            handleFreeTrial();
        } else {
            handleFreeTrialExpiration();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [freeTrialOrg, mostRecentTrial, organizationId, userLicenseAccess]);
}
